// @file Belt color utilities
import { p__, __ } from '@@/bits/intl'

type BeltColor = 'white' | 'yellow' | 'green' | 'blue' | 'red' | 'black'

const BELTS: BeltColor[] = ['yellow', 'green', 'blue', 'red', 'black']

const BELT_TEXTS_MAP: Record<BeltColor, string> = {
  white: __('White Belt'),
  yellow: __('Yellow Belt'),
  green: __('Green Belt'),
  blue: __('Blue Belt'),
  red: __('Red Belt'),
  black: __('Black Belt'),
}

const UNTRANSLATED_BELT_TEXTS_MAP: Record<BeltColor, string> = {
  white: 'White Belt',
  yellow: 'Yellow Belt',
  green: 'Green Belt',
  blue: 'Blue Belt',
  red: 'Red Belt',
  black: 'Black Belt',
}

const CERTIFIED_BELT_TEXTS_MAP: Record<BeltColor, string> = {
  white: __('Certified White Belt'),
  yellow: __('Certified Yellow Belt'),
  green: __('Certified Green Belt'),
  blue: __('Certified Blue Belt'),
  red: __('Certified Red Belt'),
  black: __('Certified Black Belt'),
}

const UNTRANSLATED_CERTIFIED_BELT_TEXTS_MAP: Record<BeltColor, string> = {
  white: 'Certified White Belt',
  yellow: 'Certified Yellow Belt',
  green: 'Certified Green Belt',
  blue: 'Certified Blue Belt',
  red: 'Certified Red Belt',
  black: 'Certified Black Belt',
}

type BeltStatusIcon =
  | 'triangle_filled'
  | 'triangle_completed'
  | 'square_filled'
  | 'square_completed'
  | 'pentagon_filled'
  | 'pentagon_completed'
  | 'hexagon_filled'
  | 'hexagon_completed'
  | 'circle_filled'
  | 'circle_completed'

const blackBeltIconUrl = 'https://padlet.net/dashboard/belt/v5_belt_icon_black.png'

function getTranslatedBeltColor(color: BeltColor): string {
  switch (color) {
    case 'white':
      return p__('Refers to the type of belt', 'White')
    case 'yellow':
      return p__('Refers to the type of belt', 'Yellow')
    case 'green':
      return p__('Refers to the type of belt', 'Green')
    case 'blue':
      return p__('Refers to the type of belt', 'Blue')
    case 'red':
      return p__('Refers to the type of belt', 'Red')
    case 'black':
      return p__('Refers to the type of belt', 'Black')
  }
}

function getBeltIconColorClass(color: Exclude<BeltColor, 'white'>): string {
  switch (color) {
    case 'yellow':
      return 'text-canary-500'
    case 'green':
      return 'text-park-500'
    case 'blue':
      return 'text-oceanic-500'
    case 'red':
      return 'text-scarlet-500'
    case 'black':
    default:
      return 'text-dark-ui-100'
  }
}

function getBeltStatusIcon(color: BeltColor, isCompleted: boolean): BeltStatusIcon {
  switch (color) {
    case 'yellow':
      return isCompleted ? 'triangle_completed' : 'triangle_filled'
    case 'green':
      return isCompleted ? 'square_completed' : 'square_filled'
    case 'blue':
      return isCompleted ? 'pentagon_completed' : 'pentagon_filled'
    case 'red':
      return isCompleted ? 'hexagon_completed' : 'hexagon_filled'
    case 'black':
      return isCompleted ? 'circle_completed' : 'circle_filled'
    case 'white':
    default:
      return 'triangle_filled'
  }
}

/*
  For the belt status icon and text color, in light mode and belt selected,
  we want to use `text-dark-text-100` at 65% opacity so that it is tinted by the background color.
  However, text opacity doesn't work that way and therefore doesn't take the background tint.
  Hence, we use the hex color code that represents the exact tint we want.
*/
function getBeltStatusIconColorClass(
  beltColor: Exclude<BeltColor, 'white'>,
  isBeltStatusAspiring: boolean,
  isBeltSelected: boolean,
): string {
  if (isBeltStatusAspiring) {
    return 'text-dark-text-400 dark:text-light-text-400'
  }

  if (isBeltSelected) {
    switch (beltColor) {
      case 'yellow':
        return 'text-[#645a36] dark:text-canary-500'
      case 'green':
        return 'text-[#3f643e] dark:text-park-500'
      case 'blue':
        return 'text-[#3e5864] dark:text-oceanic-500'
      case 'red':
        return 'text-[#644d4d] dark:text-scarlet-500'
      case 'black':
        return 'text-dark-text-100 dark:text-light-text-100'
      default:
        return 'text-dark-text-100 dark:text-light-text-100'
    }
  }

  return 'text-dark-text-100 dark:text-light-text-100'
}

function getBeltStatusTextColorClass(beltColor: Exclude<BeltColor, 'white'>, isBeltSelected: boolean): string {
  if (isBeltSelected) {
    switch (beltColor) {
      case 'yellow':
        return 'text-[#645a36] dark:text-canary-500'
      case 'green':
        return 'text-[#3f643e] dark:text-park-500'
      case 'blue':
        return 'text-[#3e5864] dark:text-oceanic-500'
      case 'red':
        return 'text-[#644d4d] dark:text-scarlet-500'
      case 'black':
      default:
        return 'text-dark-text-100 dark:text-light-text-100'
    }
  }
  return 'text-dark-text-100 dark:text-light-text-100'
}

function getBeltStatusButtonBackgroundColorStateClass(
  beltColor: Exclude<BeltColor, 'white'>,
  isBeltSelected: boolean,
): string[] {
  if (isBeltSelected) {
    switch (beltColor) {
      case 'yellow':
        return ['bg-[#FFF5D1] dark:bg-[#242116]']
      case 'green':
        return ['bg-[#DAFFD9] dark:bg-[#172416]']
      case 'blue':
        return ['bg-[#D9F3FF] dark:bg-[#161D24]']
      case 'red':
        return ['bg-[#FFE8E8] dark:bg-[#241716]']
      case 'black':
        return ['bg-[#F0F0F0] dark:bg-[#242424]']
    }
  }

  switch (beltColor) {
    case 'yellow':
      return [
        'bg-transparent',
        // Active
        'hactive:bg-[#FFF5D1]',
        'hactive:dark:bg-[#242116]',
        // Hover
        'hover-hover:hover:bg-[#FFF5D1]/50',
        'hover-hover:dark:hover:bg-[#242116]/50',
      ]
    case 'green':
      return [
        'bg-transparent',
        // Active
        'hactive:bg-[#DAFFD9]',
        'hactive:dark:bg-[#172416]',
        // Hover
        'hover-hover:hover:bg-[#DAFFD9]/50',
        'hover-hover:dark:hover:bg-[#172416]/50',
      ]
    case 'blue':
      return [
        'bg-transparent',
        // Active
        'hactive:bg-[#D9F3FF]',
        'hactive:dark:bg-[#161D24]',
        // Hover
        'hover-hover:hover:bg-[#D9F3FF]/50',
        'hover-hover:dark:hover:bg-[#161D24]/50',
      ]
    case 'red':
      return [
        'bg-transparent',
        // Active
        'hactive:bg-[#FFE8E8]',
        'hactive:dark:bg-[#241716]',
        // Hover
        'hover-hover:hover:bg-[#FFE8E8]/50',
        'hover-hover:dark:hover:bg-[#241716]/50',
      ]
    case 'black':
      return [
        'bg-transparent',
        // Active
        'hactive:bg-[#F0F0F0]',
        'hactive:dark:bg-[#242424]',
        // Hover
        'hover-hover:hover:bg-[#F0F0F0]/50',
        'hover-hover:dark:hover:bg-[#242424]/50',
      ]
  }
}

function getNextBelt(color: BeltColor): BeltColor | null {
  switch (color) {
    case 'white':
      return 'yellow'
    case 'yellow':
      return 'green'
    case 'green':
      return 'blue'
    case 'blue':
      return 'red'
    case 'red':
      return 'black'
  }
  return null
}

function getBeltPercentage(color: BeltColor): number {
  switch (color) {
    case 'yellow':
      return 10
    case 'green':
      return 3
    case 'blue':
      return 1
    case 'red':
      return 0.3
    case 'black':
      return 0.1
    case 'white':
    default:
      return 100
  }
}

function getBeltCertificateDescription(belt: BeltColor): string {
  const beltName = getBeltText(belt)
  const percentage = getBeltPercentage(belt)
  return __(
    'The Padlet Belts program honors excellence in creativity and collaboration on the Padlet platform. The {beltName} is only conferred upon the top {percentage} percent of users worldwide. Padlet assumes no responsibility for any havoc wreaked by Belt honorees in the exercise of their mastery.',
    { beltName, percentage },
  )
}

function getUntranslatedBeltCertificateDescription(belt: BeltColor): string {
  const beltName = getUntranslatedBeltText(belt)
  const percentage = getBeltPercentage(belt)
  return `The Padlet Belts program honors excellence in creativity and collaboration on the Padlet platform. The ${beltName} is only conferred upon the top ${percentage} percent of users worldwide. Padlet assumes no responsibility for any havoc wreaked by Belt honorees in the exercise of their mastery.`
}

const DEFAULT_BADGE_BACKGROUND_URL = 'https://padlet.net/dashboard/belt/v0_certificate_badge_base.png'

type BadgeBackgroundProps = {
  backgroundImage: string
  tintColor: string
  holoBadgeColor: string
  mixBlendMode: 'color' | 'overlay'
}

function getBadgeBackgroundProps(color: BeltColor): BadgeBackgroundProps {
  switch (color) {
    case 'yellow':
      return {
        backgroundImage: DEFAULT_BADGE_BACKGROUND_URL,
        tintColor: '#FFD400',
        holoBadgeColor: 'rgba(255, 212, 0, 0.5)',
        mixBlendMode: 'color',
      }
    case 'green':
      return {
        backgroundImage: DEFAULT_BADGE_BACKGROUND_URL,
        tintColor: '#6AFF00',
        holoBadgeColor: 'rgba(106, 255, 0, 0.5)',
        mixBlendMode: 'color',
      }
    case 'blue':
      return {
        backgroundImage: DEFAULT_BADGE_BACKGROUND_URL,
        tintColor: '#009DFF',
        holoBadgeColor: 'rgba(0, 157, 255, 0.5)',
        mixBlendMode: 'color',
      }
    case 'red':
      return {
        backgroundImage: DEFAULT_BADGE_BACKGROUND_URL,
        tintColor: '#E80F42',
        holoBadgeColor: 'rgba(232, 15, 66, 0.5)',
        mixBlendMode: 'color',
      }
    case 'black':
      return {
        backgroundImage: DEFAULT_BADGE_BACKGROUND_URL,
        tintColor: '#000000',
        holoBadgeColor: 'rgba(0, 0, 0, 0.5)',
        mixBlendMode: 'color',
      }
    case 'white':
    default:
      return {
        backgroundImage: DEFAULT_BADGE_BACKGROUND_URL,
        tintColor: '#D3D3D3',
        holoBadgeColor: 'rgba(211, 211, 211, 0.5)',
        mixBlendMode: 'color',
      }
  }
}

function getBeltTextColorHex(color: BeltColor, isDarkMode: boolean = false): string {
  switch (color) {
    case 'yellow':
      return '#FFAA00'
    case 'green':
      return '#12B800'
    case 'blue':
      return '#1F88FF'
    case 'red':
      return '#E80F42'
    case 'black':
      return isDarkMode ? '#DDDDDD' : '#000000'
    case 'white':
    default:
      return isDarkMode ? '#FFFFFF' : '#A3AAAC'
  }
}

function getBeltYearArcColorHex(color: BeltColor): string {
  switch (color) {
    case 'yellow':
      return '#FFE97C'
    case 'green':
      return '#96EC91'
    case 'blue':
      return '#9EDDFF'
    case 'red':
      return '#FFA1A3'
    case 'black':
      return '#B2B2B2'
    case 'white':
    default:
      return '#A3AAAC'
  }
}

function getBeltTextOutlineColorHex(color: BeltColor): string {
  switch (color) {
    case 'yellow':
      return '#FFF0C4'
    case 'green':
      return '#DCFFD3'
    case 'blue':
      return '#E6F2FF'
    case 'red':
      return '#FFE8EE'
    case 'black':
      return '#0F0F0F'
    case 'white':
    default:
      return '#A3AAAC'
  }
}

function getBeltProgressColorHex(color: BeltColor, transparency: boolean = false, darkMode: boolean = false): string {
  switch (color) {
    case 'yellow':
      if (darkMode) {
        return transparency ? 'rgba(255, 212, 18, 0.15)' : '#FFD412'
      } else {
        return transparency ? 'rgba(255, 212, 18, 0.08)' : '#FFD412'
      }
    case 'green':
      if (darkMode) {
        return transparency ? 'rgba(92, 234, 16, 0.15)' : '#5CEA10'
      } else {
        return transparency ? 'rgba(92, 234, 16, 0.08)' : '#5CEA10'
      }
    case 'blue':
      if (darkMode) {
        return transparency ? 'rgba(71, 169, 255, 0.15)' : '#47A9FF'
      } else {
        return transparency ? 'rgba(71, 169, 255, 0.08)' : '#47A9FF'
      }
    case 'red':
      if (darkMode) {
        return transparency ? 'rgba(255, 87, 36, 0.15)' : '#FF5724'
      } else {
        return transparency ? 'rgba(255, 87, 36, 0.08)' : '#FF5724'
      }
    case 'black':
      if (darkMode) {
        return transparency ? 'rgba(117, 117, 117, 0.25)' : '#757575'
      } else {
        return transparency ? 'rgba(34, 34, 34, 0.08)' : '#222222'
      }
    default:
      return '#A3AAAC'
  }
}

function getBeltCertificateShadowColorHex(color: Exclude<BeltColor, 'white'>): string {
  switch (color) {
    case 'yellow':
      return 'rgba(255, 195, 0, 0.5)'
    case 'green':
      return 'rgba(40, 168, 5, 0.5)'
    case 'blue':
      return 'rgba(49, 119, 199, 0.5)'
    case 'red':
      return 'rgba(255, 101, 137, 0.5)'
    case 'black':
      return 'rgba(17, 17, 17, 0.5)'
  }
}

function getBeltText(belt: BeltColor): string {
  return BELT_TEXTS_MAP[belt] ?? __('Belt')
}

function getUntranslatedBeltText(belt: BeltColor): string {
  return UNTRANSLATED_BELT_TEXTS_MAP[belt] ?? 'Belt'
}

function getCertifiedBeltText(belt: BeltColor): string {
  return CERTIFIED_BELT_TEXTS_MAP[belt] ?? __('Certified Belt')
}

function getUntranslatedCertifiedBeltText(belt: BeltColor): string {
  return UNTRANSLATED_CERTIFIED_BELT_TEXTS_MAP[belt] ?? 'Certified Belt'
}

function getBeltCertificateDisplayBaseImageUrl(color: Exclude<BeltColor, 'white'>): string {
  return `https://padlet.net/dashboard/belt/v2_certificate_display_base_${color}.png`
}

function getBeltCertificatePrintBaseImageUrl(color: Exclude<BeltColor, 'white'>, pageSize: 'letter' | 'a4'): string {
  return `https://padlet.net/dashboard/belt/v2_print_certificate_${pageSize}_base_${color}.png`
}
export {
    BELTS,
    blackBeltIconUrl,
    getTranslatedBeltColor,
    getBeltIconColorClass,
    getBeltStatusIcon,
    getBeltStatusIconColorClass,
    getBeltStatusTextColorClass,
    getBeltStatusButtonBackgroundColorStateClass,
    getNextBelt,
    getBeltPercentage,
    getBeltCertificateDescription,
    getUntranslatedBeltCertificateDescription,
    getBadgeBackgroundProps,
    getBeltTextColorHex,
    getBeltYearArcColorHex,
    getBeltTextOutlineColorHex,
    getBeltProgressColorHex,
    getBeltCertificateShadowColorHex,
    getBeltText,
    getUntranslatedBeltText,
    getCertifiedBeltText,
    getUntranslatedCertifiedBeltText,
    getBeltCertificateDisplayBaseImageUrl,
    getBeltCertificatePrintBaseImageUrl,
}
export type { BeltColor, BadgeBackgroundProps }
